import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, unstableSetRender } from "antd";
import jaJP from "antd/locale/ja_JP";
import { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import { AuthProvider } from "react-oidc-context";
import { BrowserRouter } from "react-router-dom";

import App from "./App";
import "./index.css";
import { ApiProvider } from "./lib/ApiProvider";
import { oidcConfig } from "./lib/auth";
import { loadConfig } from "./lib/config";
import { StartupError } from "./shell/StartupError";
import { antdTheme, applyCssVars } from "./shell/theme";

// antd の message / notification / Modal.confirm は静的関数で、既定では React 18 までの
// ReactDOM.render を使う。React 19 でそれが消えたので、描画の口を createRoot に差し替える
// (これが無いと「保存した」などの通知が一切出ない)。
const staticRoots = new WeakMap<Element | DocumentFragment, Root>();
unstableSetRender((node, container) => {
  let target = staticRoots.get(container);
  if (!target) {
    target = createRoot(container);
    staticRoots.set(container, target);
  }
  const current = target;
  current.render(node);
  return async () => {
    await new Promise((resolve) => setTimeout(resolve, 0));
    current.unmount();
    staticRoots.delete(container);
  };
});

// 素の CSS が参照する色・フォントの変数を先に置く(theme.ts が正)
applyCssVars();

const root = createRoot(document.getElementById("root")!);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

// 接続先は起動時に /config.json から読む(ビルドに焼き込まない)。
// 読めなければ何も描けないので、その旨だけ出す。
loadConfig().then(
  (config) => {
    root.render(
      <StrictMode>
        <ConfigProvider locale={jaJP} theme={antdTheme}>
          <AuthProvider {...oidcConfig(config)}>
            <QueryClientProvider client={queryClient}>
              <ApiProvider config={config}>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </ApiProvider>
            </QueryClientProvider>
          </AuthProvider>
        </ConfigProvider>
      </StrictMode>,
    );
  },
  (err: unknown) => {
    root.render(
      <ConfigProvider locale={jaJP} theme={antdTheme}>
        <StartupError message={err instanceof Error ? err.message : String(err)} />
      </ConfigProvider>,
    );
  },
);
